/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import _ from 'lodash';

import Input from '../form/Input';
import Checkbox from '../form/Checkbox';
import BottomBar from '../BottomBar';
import MultiSelectCheckbox from '../form/MultiSelectCheckbox';
import usePersistedDraft from '../hooks/usePersistedDraft';
import { updatePracticePreferences } from '../../../actions/preferences';
import { toMultiSelectOptions } from '../helpers';

const PracticeDetails3 = ({
  openLoadingPanel,
  closeLoadingPanel,
  nextPage,
  errorCallback,
  currentSubStep,
  previousPage,
  draftValues,
  onDraftChange,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  // Select options from redux store
  const diagnosisTypeOptions = useSelector(state =>
    toMultiSelectOptions(_.get(state.misc, 'diagnosisTypes', [])),
  );
  const specialityOptions = useSelector(state =>
    toMultiSelectOptions(_.get(state.misc, 'specialities', [])),
  );
  const religionOptions = useSelector(state =>
    toMultiSelectOptions(
      _.get(state.misc, 'religions', []).filter(r => r.other_yn === false),
    ),
  );

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: draftValues || {
      diagnosis_types: [],
      specialities: [],
      religions: [],
      religion_other_yn: [],
      religion_other_name: '',
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  const isReligionOtherVisible = watch('religion_other_yn')?.[0];

  const onSubmit = async values => {
    const errorMessage = intl.formatMessage({
      defaultMessage: 'There was a problem updating your practice details.',
    });

    const params = {
      diagnosis_types: _.get(values, 'diagnosis_types', []),
      specialities: _.get(values, 'specialities', []),
      religion: {
        religions: _.get(values, 'religions', []).map(id => ({ id })),
        religion_other_yn: _.get(values, 'religion_other_yn[0]', false),
        religion_other_name: _.get(values, 'religion_other_name', ''),
      },
    };

    openLoadingPanel(
      intl.formatMessage({ defaultMessage: 'Updating practice details...' }),
    );

    try {
      await dispatch(updatePracticePreferences(params));
      closeLoadingPanel();
      nextPage();
    } catch (err) {
      errorCallback(err, errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title">
        <h1>{intl.formatMessage({ defaultMessage: 'Practice Details' })}</h1>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <div className="sub-page-indicator">{`${currentSubStep + 1}/7`}</div>
      </div>

      <h3>
        {intl.formatMessage({
          defaultMessage:
            'Which topic(s) do you have a strong understanding of/experience with?',
        })}
      </h3>

      <h4 style={{ marginTop: 40, marginBottom: 20 }}>
        {intl.formatMessage({ defaultMessage: 'Diagnoses' })}
      </h4>
      <div className="input-row">
        <Controller
          name="diagnosis_types"
          control={control}
          rules={{
            validate: v =>
              v && v.length > 0
                ? true
                : intl.formatMessage({ defaultMessage: 'Required' }),
          }}
          render={({ field }) => (
            <MultiSelectCheckbox
              {...field}
              options={diagnosisTypeOptions}
              error={errors.diagnosis_types?.message}
            />
          )}
        />
      </div>

      <h4 style={{ marginTop: 10, marginBottom: 20 }}>
        {intl.formatMessage({ defaultMessage: 'Specialties' })}
      </h4>
      <div className="input-row">
        <Controller
          name="specialities"
          control={control}
          rules={{
            validate: v =>
              v && v.length > 0
                ? true
                : intl.formatMessage({ defaultMessage: 'Required' }),
          }}
          render={({ field }) => (
            <MultiSelectCheckbox
              {...field}
              options={specialityOptions}
              error={errors.specialities?.message}
            />
          )}
        />
      </div>

      <h4 style={{ marginTop: 10, marginBottom: 20 }}>
        {intl.formatMessage({
          defaultMessage: 'Religious and spiritual knowledge',
        })}
      </h4>
      <div className="input-row">
        <Controller
          name="religions"
          control={control}
          render={({ field }) => (
            <MultiSelectCheckbox
              {...field}
              options={religionOptions}
              style={{ marginBottom: -35 }}
            />
          )}
        />

        <Controller
          name="religion_other_yn"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({ defaultMessage: 'Other' }),
                },
              ]}
              style={{ marginTop: 40 }}
            />
          )}
        />

        {isReligionOtherVisible && (
          <Controller
            name="religion_other_name"
            control={control}
            render={({ field }) => (
              // eslint-disable-next-line formatjs/no-literal-string-in-jsx, react/jsx-no-literals
              <Input {...field} label={<span>&zwnj;</span>} />
            )}
          />
        )}
      </div>

      <BottomBar
        openLoadingPanel={openLoadingPanel}
        closeLoadingPanel={closeLoadingPanel}
        nextPage={nextPage}
        errorCallback={errorCallback}
        currentSubStep={currentSubStep}
        previousPage={previousPage}
      />
    </form>
  );
};

PracticeDetails3.propTypes = {
  openLoadingPanel: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  errorCallback: PropTypes.func.isRequired,
  currentSubStep: PropTypes.number.isRequired,
  previousPage: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
};

export default PracticeDetails3;
